import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import EditingReturnBanner, { backToLabelFor } from './EditingReturnBanner'
import { useFieldFocusHandler } from '../../hooks/useFieldFocusHandler'
import {
  ArrowUpTrayIcon,
  CheckIcon,
  DocumentTextIcon,
  EyeIcon,
  PencilSquareIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { toast } from 'sonner'
import Card from '../ui/Card'
import Button from '../ui/Button'
import PageLoader from '../ui/PageLoader'
import PageHeader from '../layout/PageHeader'
import ConfirmDialog from '../ui/ConfirmDialog'
import ColumnSelectorDialog from './ColumnSelectorDialog'
import UploadWeldDataDialog, {
  type ExistingWeldSummary,
  type UploadCompletePayload
} from './UploadWeldDataDialog'
import UnifiedWeldUploadDialog from './weldUpload/UnifiedWeldUploadDialog'
import type { UploadFormat } from '../../utils/weldUpload/types'
import { useProjects } from '../../contexts/ProjectsContext'
import { useCatalog } from '../../contexts/CatalogContext'
import { useWpsSections } from '../../contexts/WpsSectionsContext'
import WeldParametersUploadedView from './WeldParametersUploadedView'
import WeldParametersManualView, { ManualRow, ManualAgg } from './WeldParametersManualView'
import AlxSummaryInfoBanner from './weldUpload/AlxSummaryInfoBanner'
import { sortAlxPassLabels } from '../../utils/alx/sortPasses'
import {
  expandPassSequenceToLabels,
  passSectionInputForBevel
} from '../../utils/passSequenceExpanded'
import {
  aggregateFromServerResponse,
  type AggregateResult,
  type ParameterKey,
  type Torch
} from '../../utils/weldParameterAggregator'
import { resolveWeldParametersPassLabels } from '../../utils/weldParametersPassLabels'
import {
  loadVisibleColumns,
  saveVisibleColumns,
  VisibleColumns
} from '../../utils/weldParametersColumnLayout'
import {
  deleteWeldTelemetry,
  fetchExistingWeldTelemetry,
  fetchWeldTelemetryAggregate
} from '../../utils/weldTelemetryClient'
import { needsData360PassMapping } from '../../utils/floorSheetWeldParams'
import {
  formatStationDirectionLabel,
  type WeldDirection
} from '../../utils/weldTxtFilenameParser'
import { bevelLabelForIndex } from '../../utils/bevelOptionLabels'
import {
  buildDescriptionsByHeat,
  normalizeBevelIndex,
  resolvePupDisplayLabel,
  resolvePupHeatsFromWeld
} from '../../utils/pupHeatCatalog'
import WeldParametersHeaderPills, {
  weldParametersHeaderHasAny
} from './weldParameters/WeldParametersHeaderPills'
import WeldParametersToleranceBar from './weldParameters/WeldParametersToleranceBar'
import SwapIwmBugTypeToggle from './weldParameters/SwapIwmBugTypeToggle'
import WeldParametersOverflowMenu from './weldParameters/WeldParametersOverflowMenu'
import {
  formatToleranceInput,
  readLimitsModeFromWeldSection,
  readToleranceFromWeldSection
} from '../../utils/pqr/weldParamsWpsLimits'
import { readSwapIwmBugType } from '../../utils/pqr/swapIwmBugType'
import { applyPqrToleranceToTriple } from '../../utils/pqr/applyPqrTolerance'
import { exportWeldParametersExcel } from '../../utils/pqr/exportPqrExcel'
import { formatExcelNumber } from '../../utils/pqr/buildPqrExcelWorkbook'
import { WELDING_PARAMETER_COLUMN_DEFS } from '../../utils/pqrWeldingParametersMapper'
import { resolvePreheatCellsForPass } from '../../utils/preheatInterpassForPass'
import { useData360FloorSheet } from '../../hooks/useData360FloorSheet'
import {
  DATA_360_DISPLAY_NAME,
  formatData360DisplayText
} from '../../utils/data360DisplayName'
import { buildWeldParamsSectionAfterTelemetryReplace } from '../../utils/weldParamsTelemetryReset'

const DEBOUNCE_MS = 300
const CONFIRM_DESCRIPTION =
  'Switch to manual editing? Your changes will be staged in a draft — review and click Save to overwrite the uploaded TXT data, or Cancel to keep the upload as-is.'
const DELETE_CONFIRM_DESCRIPTION =
  'Permanently delete the uploaded TXT data and all telemetry samples for this weld? This cannot be undone.'
const CANCEL_DRAFT_CONFIRM_DESCRIPTION =
  'Discard your manual edits and return to the uploaded TXT view? Any unsaved changes will be lost.'
const TELEMETRY_REPLACE_CONFIRM_DESCRIPTION = (weldLabel: string) =>
  `Re-importing will replace all telemetry for Weld ${weldLabel}, reset tolerance to 0%, and clear all Pass Analytics selections and saved min/max overrides.`
const DATA360_REIMPORT_CONFIRM_DESCRIPTION = (weldLabel: string) =>
  `Re-importing will replace all telemetry from ${DATA_360_DISPLAY_NAME} for Weld ${weldLabel}, reset tolerance to 0%, and clear all Pass Analytics selections and saved min/max overrides.`

type Mode = 'manual' | 'upload'

export default function WeldParameters() {
  const { id, pipeId, weldId } = useParams()
  const navigate = useNavigate()
  // Slice 08 of pqr-preview-field-navigation. When the user clicks a
  // Welding Parameters cell on the PQR Preview canvas they're routed
  // here with `?focusField=<columnKey>&focusPass=<passLabel>&returnTo=…`.
  // We scroll the page's first matching cell into view + flash it +
  // try to focus an inner control. The destination DOM tags each
  // (pass, column) cell with `data-focus-field-cell="<col>:<pass>"`.
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const syncFromPassAnalytics = searchParams.get('sync') === '1'
  const returnTo = searchParams.get('returnTo')
  const pageRootRef = useRef<HTMLDivElement>(null)
  useFieldFocusHandler({
    ready: true,
    resolveTarget: (params) => {
      if (!params.focusField) return null
      const root = pageRootRef.current ?? document
      const composite =
        params.focusField + (params.focusPass ? `:${params.focusPass}` : '')
      const byCell = root.querySelector(
        `[data-focus-field-cell="${composite}"]`
      ) as HTMLElement | null
      if (byCell) return byCell
      // Fall back to the column-level header when no specific (pass, col)
      // cell is in the DOM yet (e.g. manual draft still loading).
      const byCol = root.querySelector(
        `[data-focus-field="${params.focusField}"]`
      ) as HTMLElement | null
      return byCol
    }
  })
  const { getProject } = useProjects()
  const { getLabelsMerged, addLabel } = useCatalog()
  const { getSection, updateSection, loading: sectionsLoading, sections, refresh, syncSectionsVersion } = useWpsSections()
  const project = id ? getProject(id) : undefined
  const pipeSet = project?.pipeSets.find((p) => p.id === pipeId)
  const data360 = useData360FloorSheet(id, pipeId, project?.data360ProjectId)

  const [wpsNumber, setWpsNumber] = useState('')
  const [weldNumber, setWeldNumber] = useState('')
  /**
   * Pipe-set–wide station + direction parsed from the most recent TXT
   * filename (`14INCH ST1 CCW.txt` → `1`, `'CCW'`). Persisted alongside the
   * other pipe-set invariants on the `meta` section so every weld in the
   * spread renders the same subtitle. Both `null` when no TXT batch has
   * landed yet.
   */
  const [stationNumber, setStationNumber] = useState<number | null>(null)
  const [direction, setDirection] = useState<WeldDirection | null>(null)
  const [mode, setMode] = useState<Mode>('manual')
  const [manualRows, setManualRows] = useState<ManualRow[]>([])
  const [uploadedAggregate, setUploadedAggregate] = useState<AggregateResult | null>(null)
  const [uploadedFilename, setUploadedFilename] = useState<string>('')
  const [uploadedAt, setUploadedAt] = useState<string>('')
  const [passMappings, setPassMappings] = useState<Record<string, string>>({})
  const [showConfirm, setShowConfirm] = useState(false)
  const [showColumns, setShowColumns] = useState(false)
  const [showReuploadMode, setShowReuploadMode] = useState(false)
  const [uploadFormat, setUploadFormat] = useState<UploadFormat>('telemetry-txt')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showCancelDraftConfirm, setShowCancelDraftConfirm] = useState(false)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [saveDraftBusy, setSaveDraftBusy] = useState(false)
  const [hasServerUpload, setHasServerUpload] = useState(false)
  /**
   * True while the user is in the manual-edit DRAFT — i.e. they switched
   * away from an existing upload but haven't clicked Save yet. While true:
   *   - Cell edits update `manualRows` in memory but the debounced
   *     auto-save is suppressed (Save / Cancel are the explicit commit
   *     and revert affordances).
   *   - The uploaded aggregate is kept in memory so Cancel can restore
   *     it without a server refetch.
   *   - The header swaps its action buttons to a clean Cancel / Save
   *     pair, hiding Re-upload / Delete / Pencil to avoid mid-draft
   *     surprises.
   *
   * `false` for fresh-weld manual entry (no upload to revert to) and for
   * the permanent post-Save manual mode — both keep today's auto-save
   * behaviour.
   */
  const [manualDraft, setManualDraft] = useState(false)
  /**
   * Snapshot of the upload state captured at the moment the user
   * confirmed the switch into draft mode. Used by Cancel to restore the
   * exact view the user was looking at — no server round-trip, no
   * loss of pass-mapping metadata.
   */
  const [draftSnapshot, setDraftSnapshot] = useState<{
    aggregate: AggregateResult
    sourceFilename: string
    uploadedAt: string
    passMappings: Record<string, string>
  } | null>(null)
  const [localLoaded, setLocalLoaded] = useState(false)
  const [remoteLoaded, setRemoteLoaded] = useState(false)
  /**
   * True while the background `/aggregate` refetch is in flight on a mount
   * where we already had cached content to render. Drives the small
   * `wp-refreshing` indicator next to the page-header subtitle so users can
   * see why their just-saved Pass Analytics edits are about to appear without
   * the page wiping itself to a skeleton. Independent of `remoteLoaded` so
   * the skeleton-vs-content gate keeps its single-mount semantics.
   */
  const [refreshing, setRefreshing] = useState(false)
  /**
   * True iff the local-load effect found a persisted blob with an explicit
   * `source` field. Distinguishes "user chose manual" (respect their choice)
   * from "no local cache at all" (server-first fallback).
   */
  const [hasExplicitLocalSource, setHasExplicitLocalSource] = useState(false)
  const [visibleColumns, setVisibleColumns] = useState<VisibleColumns>(() =>
    loadVisibleColumns()
  )
  const [tolerance, setTolerance] = useState('0%')
  const [savedTolerance, setSavedTolerance] = useState('0%')
  const [toleranceSaveBusy, setToleranceSaveBusy] = useState(false)
  const [swapIwmBugType, setSwapIwmBugType] = useState(false)
  const [swapIwmSaveBusy, setSwapIwmSaveBusy] = useState(false)
  const [iwmRootDialogOpen, setIwmRootDialogOpen] = useState(false)
  const [exportBusy, setExportBusy] = useState(false)
  const [showReuploadConfirm, setShowReuploadConfirm] = useState(false)
  const [showData360ReimportConfirm, setShowData360ReimportConfirm] = useState(false)
  const [reimportBusy, setReimportBusy] = useState(false)
  // We have something to render as soon as either an upload aggregate is in
  // hand OR the user has explicitly chosen manual mode (even if rows are
  // empty). When that's true we keep the existing UI on screen during the
  // background server refetch instead of flashing the skeleton.
  const hasContentToRender =
    uploadedAggregate !== null ||
    (hasExplicitLocalSource && mode === 'manual')
  const loading = !localLoaded || (!remoteLoaded && !hasContentToRender)
  const stationDirectionLabel = formatStationDirectionLabel(stationNumber, direction)

  const handleVisibleColumnsChange = useCallback((next: VisibleColumns) => {
    setVisibleColumns(next)
    saveVisibleColumns(next)
  }, [])

  const floorSheetWeld = useMemo(() => {
    if (!weldId) return null
    const floorData = getSection('floorsheet')
    const welds = floorData && Array.isArray(floorData.welds) ? floorData.welds : []
    const currentWeld = welds.find(
      (w: unknown) => String((w as { id?: unknown })?.id) === String(weldId)
    )
    if (!currentWeld || typeof currentWeld !== 'object') return null
    return currentWeld as { bevelIndex?: unknown; rootEquipment?: unknown }
  }, [getSection, sections, weldId])

  const weldBevelIndex = useMemo(
    () => (floorSheetWeld ? normalizeBevelIndex(floorSheetWeld.bevelIndex) : null),
    [floorSheetWeld]
  )

  /** IWM Root mapping only applies to IWM / Automatic — not Semi-Automatic (Root Misfire). */
  const showIwmRootMapping = floorSheetWeld?.rootEquipment !== 'iwm_and_rc'

  const wpsPassLabels = useMemo(() => {
    const passSection = getSection('pass')
    const bevelInput =
      weldBevelIndex !== null
        ? passSectionInputForBevel(passSection, weldBevelIndex)
        : passSectionInputForBevel(passSection, 0)
    return expandPassSequenceToLabels(bevelInput)
  }, [getSection, weldBevelIndex, sections])

  const manualRowsRef = useRef<ManualRow[]>([])
  manualRowsRef.current = manualRows
  const modeRef = useRef<Mode>('manual')
  modeRef.current = mode
  const uploadedAggregateRef = useRef<AggregateResult | null>(null)
  uploadedAggregateRef.current = uploadedAggregate
  const manualDraftRef = useRef(false)
  manualDraftRef.current = manualDraft
  /**
   * Flips to `true` the first time the user touches a cell or torch
   * dropdown after entering draft mode. Cleared on draft entry, Cancel
   * confirmation, and Save. Lets us skip the "discard your edits?"
   * confirm when the user clicks Cancel without changing anything.
   */
  const draftDirtyRef = useRef(false)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!id || !pipeId || !weldId || sectionsLoading) return
    if (needsData360PassMapping(getSection, weldId)) {
      navigate(
        `/projects/${encodeURIComponent(id)}/next/${encodeURIComponent(pipeId)}/floorsheet?weldTab=${encodeURIComponent(weldId)}&data360Rename=1`,
        { replace: true }
      )
    }
  }, [id, pipeId, weldId, sectionsLoading, getSection, navigate])

  // Wait for the WpsSections HTTP load to finish before hydrating local state
  // from the section blob. Without this gate the effect would run once with
  // empty sections (→ "no blob, default to manual") and then re-run when the
  // real sections arrive, racing against the remote-load effect below and
  // double-consuming jest mock queues in tests.
  useEffect(() => {
    if (!pipeId || !weldId || sectionsLoading) return
    setLocalLoaded(false)

    const metaData = getSection('meta')
    if (metaData) {
      setWpsNumber(metaData.wpsNumber || '')
      setSwapIwmBugType(readSwapIwmBugType(metaData))
      const rawStation = metaData.station
      const parsedStation =
        typeof rawStation === 'number' && Number.isFinite(rawStation)
          ? rawStation
          : typeof rawStation === 'string' && rawStation.trim() !== ''
            ? (Number.isFinite(Number(rawStation)) ? Number(rawStation) : null)
            : null
      setStationNumber(parsedStation)
      const rawDirection = metaData.direction
      setDirection(rawDirection === 'CCW' || rawDirection === 'CW' ? rawDirection : null)
    } else {
      setStationNumber(null)
      setDirection(null)
      setSwapIwmBugType(false)
    }

    const floorData = getSection('floorsheet')
    if (floorData) {
      try {
        const welds = Array.isArray(floorData.welds) ? floorData.welds : []
        const currentWeld = welds.find((w: any) => String(w.id) === String(weldId))
        if (currentWeld) {
          const passRecords = Array.isArray(currentWeld.passRecords) ? currentWeld.passRecords : []
          const fromPass = passRecords.find(
            (r: any) => String(r?.weldId || '').trim() || String(r?.weldNumber || '').trim()
          )
          setWeldNumber(
            String(
              currentWeld.weldId ||
                currentWeld.weldNumber ||
                fromPass?.weldId ||
                fromPass?.weldNumber ||
                ''
            )
          )
        }
      } catch {
        // ignore — header subtitle is best-effort
      }
    }

    const persisted = getSection(`weldparams.${weldId}`)
    if (persisted && typeof persisted === 'object') {
      const persistedTolerance =
        typeof persisted.tolerance === 'string'
          ? persisted.tolerance
          : formatToleranceInput(readToleranceFromWeldSection(persisted))
      setTolerance(persistedTolerance)
      setSavedTolerance(persistedTolerance)
      const source = persisted.source as Mode | undefined
      const aggregate = isAggregateShape(persisted.aggregate)
        ? (persisted.aggregate as AggregateResult)
        : null
      if (source === 'manual') {
        setMode('manual')
        const rows = Array.isArray(persisted.rows) ? (persisted.rows as ManualRow[]) : []
        setManualRows(rows)
        setUploadedAggregate(null)
        setUploadedFilename('')
        setUploadedAt('')
        setPassMappings({})
        setHasExplicitLocalSource(true)
        setLocalLoaded(true)
        return
      }
      if (source === 'upload' || (!source && aggregate)) {
        setMode('upload')
        if (!syncFromPassAnalytics) {
          setUploadedAggregate(aggregate)
        } else {
          setUploadedAggregate(null)
        }
        setUploadedFilename(String(persisted.sourceFilename || ''))
        setUploadedAt(String(persisted.uploadedAt || ''))
        setPassMappings(
          persisted.passMappings && typeof persisted.passMappings === 'object'
            ? (persisted.passMappings as Record<string, string>)
            : {}
        )
        const fmt = (persisted as { uploadFormat?: unknown }).uploadFormat
        setUploadFormat(fmt === 'alx-summary' ? 'alx-summary' : 'telemetry-txt')
        setManualRows([])
        setHasExplicitLocalSource(true)
        setLocalLoaded(true)
        return
      }
    }

    setMode('manual')
    setUploadedAggregate(null)
    setUploadedFilename('')
    setUploadedAt('')
    setPassMappings({})
    setManualRows([])
    setHasExplicitLocalSource(false)
    setTolerance('0%')
    setSavedTolerance('0%')
    setLocalLoaded(true)
  }, [pipeId, weldId, getSection, sectionsLoading, syncFromPassAnalytics])

  // Server-first hydration: probe `existing` for the source-of-truth label
  // (so "Re-upload TXT" reflects the backend even when the local cache is
  // empty), then — when the server has an upload AND the user has NOT
  // explicitly chosen manual mode — ALWAYS pull the server-computed
  // aggregate so Pass-Analytics edits made between mounts land back here on
  // the next page visit. The previous `!uploadedAggregateRef.current` guard
  // is intentionally gone: it created a stale-cache trap where curated
  // Min/Max snapshots saved on the Pass Analytics page never appeared in the
  // Weld Parameters table until the user manually cleared the section blob.
  //
  // The local blob continues to provide a fast first paint; the background
  // refetch overwrites it in place. When we already have content to render,
  // `refreshing` toggles a small "Refreshing…" badge instead of flipping the
  // page back to a skeleton.
  useEffect(() => {
    if (!id || !pipeId || !weldId || !localLoaded) return
    let cancelled = false
    setRemoteLoaded(false)
    const hadCachedContent = uploadedAggregateRef.current !== null
    if (hadCachedContent) setRefreshing(true)
    ;(async () => {
      try {
        const existing = await fetchExistingWeldTelemetry({
          projectId: id,
          pipeSetId: pipeId,
          weldId
        })
        if (cancelled) return
        const hasUpload = Boolean(existing?.existingUpload)
        setHasServerUpload(hasUpload)

        const userChoseManual = hasExplicitLocalSource && modeRef.current === 'manual'
        if (hasUpload && !userChoseManual) {
          const remote = await fetchWeldTelemetryAggregate({
            projectId: id,
            pipeSetId: pipeId,
            weldId
          })
          if (cancelled) return
          const aggregate = aggregateFromServerResponse(remote)
          const sourceFilename =
            remote.sourceFilename || existing.existingUpload?.sourceFilename || ''
          const uploadedAtIso =
            remote.uploadedAt || existing.existingUpload?.uploadedAt || ''
          const mappings =
            remote.passMappings ||
            existing.existingUpload?.passMappings ||
            {}
          setUploadedAggregate(aggregate)
          setUploadedFilename(sourceFilename)
          setUploadedAt(uploadedAtIso)
          setPassMappings(mappings)
          setMode('upload')
          const existingSection = getSection(`weldparams.${weldId}`)
          const existingRecord =
            existingSection && typeof existingSection === 'object'
              ? (existingSection as Record<string, unknown>)
              : {}
          const existingData360 =
            existingRecord.data360 && typeof existingRecord.data360 === 'object'
              ? (existingRecord.data360 as Record<string, unknown>)
              : undefined
          await updateSection(`weldparams.${weldId}`, {
            ...existingRecord,
            source: 'upload',
            aggregate,
            sourceFilename,
            uploadedAt: uploadedAtIso,
            passMappings: mappings,
            ...(existingData360 ? { data360: existingData360 } : {})
          })
          if (syncFromPassAnalytics) {
            const nextParams = new URLSearchParams(searchParams)
            nextParams.delete('sync')
            const nextSearch = nextParams.toString()
            navigate(
              { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' },
              { replace: true }
            )
          }
        }
      } catch (err) {
        if (!cancelled) {
          setHasServerUpload(false)
          toast.error(
            err instanceof Error ? err.message : 'Failed to refresh welding parameters'
          )
        }
      } finally {
        if (!cancelled) {
          setRemoteLoaded(true)
          setRefreshing(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, pipeId, weldId, localLoaded, location.pathname, syncFromPassAnalytics])

  const scheduleSave = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    debounceTimerRef.current = setTimeout(() => {
      if (modeRef.current !== 'manual' || !weldId) return
      // Draft sessions stage edits in memory only — Save / Cancel are
      // the explicit commit and revert paths. Letting the debounce fire
      // here would silently persist a "manual mode" blob and turn Cancel
      // into a destructive no-op.
      if (manualDraftRef.current) return
      void updateSection(`weldparams.${weldId}`, {
        source: 'manual',
        rows: manualRowsRef.current
      })
    }, DEBOUNCE_MS)
  }, [updateSection, weldId])

  const handleCellChange = useCallback(
    (pass: string, param: ParameterKey, agg: ManualAgg, value: number | null) => {
      if (param === 'preheat') return
      if (manualDraftRef.current) draftDirtyRef.current = true
      setManualRows((prev) => upsertCellValue(prev, pass, param, agg, value))
      scheduleSave()
    },
    [scheduleSave]
  )

  const handleTorchChange = useCallback(
    (pass: string, torch: Torch) => {
      if (manualDraftRef.current) draftDirtyRef.current = true
      setManualRows((prev) => upsertRowTorch(prev, pass, torch))
      scheduleSave()
    },
    [scheduleSave]
  )

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    }
  }, [])

  const handleEnterManually = useCallback(() => setShowConfirm(true), [])

  /**
   * Re-enter edit mode on a weld that is ALREADY in manual mode (post-save,
   * read-only view). Unlike `handleEnterManually`, no confirmation is
   * needed because the user isn't switching SOURCES (manual ↔ upload) —
   * they're just toggling between the read-only viewer and the editable
   * form. Pencil rendered next to the eye buttons whenever the manual
   * matrix has rows to edit and the user isn't already drafting.
   */
  const handleReEnterManualEdit = useCallback(() => {
    draftDirtyRef.current = false
    setManualDraft(true)
  }, [])

  const handleConfirmSwitch = useCallback(() => {
    if (!weldId) return
    const rows: ManualRow[] = uploadedAggregate
      ? uploadedAggregate.rows.map((r) => ({
          pass: r.pass,
          torch: defaultManualTorch(r.torches),
          values: r.values
        }))
      : []
    setManualRows(rows)
    setMode('manual')
    setShowConfirm(false)

    if (uploadedAggregate) {
      // Enter DRAFT mode: keep the upload aggregate + metadata in memory
      // so Cancel can restore it without a server hit, and DON'T persist
      // `source: 'manual'` yet — Save is the only path that overwrites
      // the section blob and disconnects from the TXT.
      setDraftSnapshot({
        aggregate: uploadedAggregate,
        sourceFilename: uploadedFilename,
        uploadedAt,
        passMappings
      })
      draftDirtyRef.current = false
      setManualDraft(true)
      return
    }

    // First-time manual entry (no upload to revert to) keeps the old
    // auto-save flow — there's nothing to cancel back to.
    setDraftSnapshot(null)
    setManualDraft(false)
    void updateSection(`weldparams.${weldId}`, {
      source: 'manual',
      rows
    })
  }, [uploadedAggregate, uploadedFilename, uploadedAt, passMappings, weldId, updateSection])

  const handleCancelSwitch = useCallback(() => setShowConfirm(false), [])

  const handleSaveManualDraft = useCallback(async () => {
    if (!weldId) return
    setSaveDraftBusy(true)
    try {
      // Flush any pending debounce timer to avoid a stale "manual rows"
      // write landing AFTER this Save and clobbering subsequent edits.
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }
      await updateSection(`weldparams.${weldId}`, {
        source: 'manual',
        rows: manualRowsRef.current
      })
      // Commit: drop the upload snapshot from the in-memory view so the
      // manual table is now the source of truth. The user can still
      // recover the TXT data via Re-upload (and the server-side
      // telemetry remains until they hit Delete).
      setUploadedAggregate(null)
      setUploadedFilename('')
      setUploadedAt('')
      setPassMappings({})
      setDraftSnapshot(null)
      draftDirtyRef.current = false
      setManualDraft(false)
      setHasExplicitLocalSource(true)
      toast.success('Manual parameter values saved')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaveDraftBusy(false)
    }
  }, [weldId, updateSection])

  const handleCancelManualDraft = useCallback(() => {
    if (!draftSnapshot) {
      draftDirtyRef.current = false
      setManualDraft(false)
      setShowCancelDraftConfirm(false)
      return
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
    setMode('upload')
    setManualRows([])
    setUploadedAggregate(draftSnapshot.aggregate)
    setUploadedFilename(draftSnapshot.sourceFilename)
    setUploadedAt(draftSnapshot.uploadedAt)
    setPassMappings(draftSnapshot.passMappings)
    setDraftSnapshot(null)
    draftDirtyRef.current = false
    setManualDraft(false)
    setShowCancelDraftConfirm(false)
  }, [draftSnapshot])

  // Only prompt for confirmation when the user has actually touched at
  // least one cell — clicking Cancel on an untouched draft should just
  // bounce straight back to the upload view.
  const handleRequestCancelDraft = useCallback(() => {
    if (!draftDirtyRef.current) {
      handleCancelManualDraft()
      return
    }
    setShowCancelDraftConfirm(true)
  }, [handleCancelManualDraft])

  const handleReuploadComplete = useCallback(
    async ({
      aggregate,
      sourceFilename,
      passMappings: nextMappings,
      uploadedAt: uploadedAtIso,
      filenameMeta,
      uploadFormat: nextFormat,
      sourceFilenames
    }: UploadCompletePayload) => {
      const resolvedFormat: UploadFormat =
        nextFormat === 'alx-summary' ? 'alx-summary' : 'telemetry-txt'
      setUploadedAggregate(aggregate)
      setUploadedFilename(sourceFilename)
      setUploadedAt(uploadedAtIso)
      setPassMappings(nextMappings)
      setUploadFormat(resolvedFormat)
      setManualRows([])
      setMode('upload')
      setShowReuploadMode(false)
      setHasServerUpload(resolvedFormat === 'telemetry-txt')

      // Promote filename-parsed station/direction to the pipe-set–wide
      // `meta` section so every weld in the spread shows the same header
      // chip. Only overwrite when the new TXT actually carries the token —
      // a `null` (filename had no `ST*` or `CCW`/`CW`) leaves the existing
      // value intact rather than wiping it out.
      const existingMeta = (getSection('meta') ?? {}) as Record<string, unknown>
      const nextMetaPatch: Record<string, unknown> = {}
      if (filenameMeta.station !== null && filenameMeta.station !== existingMeta.station) {
        nextMetaPatch.station = filenameMeta.station
        setStationNumber(filenameMeta.station)
      }
      if (filenameMeta.direction && filenameMeta.direction !== existingMeta.direction) {
        nextMetaPatch.direction = filenameMeta.direction
        setDirection(filenameMeta.direction)
      }
      if (Object.keys(nextMetaPatch).length > 0) {
        await updateSection('meta', { ...existingMeta, ...nextMetaPatch })
      }

      if (weldId) {
        const existingSection = getSection(`weldparams.${weldId}`)
        const existingRecord =
          existingSection && typeof existingSection === 'object'
            ? (existingSection as Record<string, unknown>)
            : {}
        await updateSection(
          `weldparams.${weldId}`,
          buildWeldParamsSectionAfterTelemetryReplace({
            existingRecord,
            aggregate,
            sourceFilename,
            uploadedAt: uploadedAtIso,
            passMappings: nextMappings,
            uploadFormat: resolvedFormat,
            sourceFilenames
          })
        )
        setTolerance('0%')
        setSavedTolerance('0%')
      }
    },
    [weldId, updateSection, getSection]
  )

  const toleranceBoundary = useMemo(
    () => readToleranceFromWeldSection({ tolerance: savedTolerance }),
    [savedTolerance]
  )
  const pqrLimitsMode = useMemo(() => {
    const section = weldId ? getSection(`weldparams.${weldId}`) : null
    return readLimitsModeFromWeldSection(section)
  }, [getSection, weldId, savedTolerance])
  const toleranceDirty = tolerance.trim() !== savedTolerance.trim()

  const handleSaveTolerance = useCallback(async () => {
    if (!weldId) return
    setToleranceSaveBusy(true)
    try {
      const existingSection = getSection(`weldparams.${weldId}`)
      const existingRecord =
        existingSection && typeof existingSection === 'object'
          ? (existingSection as Record<string, unknown>)
          : {}
      const normalized = tolerance.trim() || '0%'
      await updateSection(`weldparams.${weldId}`, {
        ...existingRecord,
        tolerance: normalized.endsWith('%') ? normalized : `${normalized}%`,
        pqrLimitsMode: 'minmax'
      })
      setSavedTolerance(normalized.endsWith('%') ? normalized : `${normalized}%`)
      setTolerance(normalized.endsWith('%') ? normalized : `${normalized}%`)
      toast.success('Tolerance saved')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save tolerance')
    } finally {
      setToleranceSaveBusy(false)
    }
  }, [weldId, tolerance, getSection, updateSection])

  const passAnalyticsAvailable =
    uploadFormat === 'telemetry-txt' && hasServerUpload

  const handleConfirmDelete = useCallback(async () => {
    if (!id || !pipeId || !weldId) return
    setDeleteBusy(true)
    try {
      await deleteWeldTelemetry({ projectId: id, pipeSetId: pipeId, weldId })
      setUploadedAggregate(null)
      setUploadedFilename('')
      setUploadedAt('')
      setPassMappings({})
      setMode('manual')
      setManualRows([])
      setHasServerUpload(false)
      await updateSection(`weldparams.${weldId}`, { source: 'manual', rows: [] })
      toast.success('Uploaded TXT data deleted')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Delete failed')
    } finally {
      setDeleteBusy(false)
      setShowDeleteConfirm(false)
    }
  }, [id, pipeId, weldId, updateSection])

  // Show the re-upload warning banner when EITHER local cache (mode==='upload')
  // OR the server (hasServerUpload) indicates an upload exists for this weld.
  // This prevents the cache-empty drift case where the WeldParameters page
  // would otherwise treat a server-known upload as a first-time upload.
  const existingSummary: ExistingWeldSummary | null =
    mode === 'upload' || hasServerUpload
      ? {
          source: 'upload',
          sourceFilename: uploadedFilename || undefined,
          uploadedAt: uploadedAt || undefined,
          passMappings
        }
      : null

  const uploadedLabel = mode === 'upload' || hasServerUpload

  const data360Meta = useMemo(() => {
    if (!weldId) return null
    const section = getSection(`weldparams.${weldId}`)
    if (!section || typeof section !== 'object') return null
    const raw = (section as Record<string, unknown>).data360
    if (!raw || typeof raw !== 'object') return null
    const meta = raw as Record<string, unknown>
    const productionWeldId =
      typeof meta.productionWeldId === 'number' ? meta.productionWeldId : null
    if (!productionWeldId) return null
    return {
      productionWeldId,
      weldName: String(meta.weldName ?? weldNumber ?? '').trim(),
      jobNumber: typeof meta.jobNumber === 'string' ? meta.jobNumber : undefined
    }
  }, [weldId, getSection, sections, weldNumber])

  const isData360Upload = Boolean(data360Meta?.productionWeldId)

  const handleData360Reimport = useCallback(async () => {
    if (!id || !pipeId || !weldId || !data360Meta) return
    setReimportBusy(true)
    try {
      const result = await data360.runWeldImport({
        weldRowId: weldId,
        productionWeldId: data360Meta.productionWeldId,
        weldName: data360Meta.weldName || weldNumber,
        jobNumber: data360Meta.jobNumber
      })
      syncSectionsVersion(result.sectionsVersion)
      const { sections: latestSections } = await refresh()
      const remote = await fetchWeldTelemetryAggregate({
        projectId: id,
        pipeSetId: pipeId,
        weldId
      })
      const aggregate = aggregateFromServerResponse(remote)
      const weldParamsKey = `weldparams.${weldId}`
      const section = latestSections[weldParamsKey] ?? getSection(weldParamsKey)
      const sectionRecord =
        section && typeof section === 'object' ? (section as Record<string, unknown>) : {}
      setUploadedAggregate(aggregate)
      setUploadedFilename(result.sourceFilename)
      setUploadedAt(result.uploadedAt)
      setPassMappings(result.passMappings)
      setTolerance(typeof sectionRecord.tolerance === 'string' ? sectionRecord.tolerance : '0%')
      setSavedTolerance(typeof sectionRecord.tolerance === 'string' ? sectionRecord.tolerance : '0%')
      setMode('upload')
      setHasServerUpload(true)
      toast.success(
        result.replaced
          ? `Replaced weld data from ${DATA_360_DISPLAY_NAME} (${result.sampleCount} samples).`
          : `Imported weld data from ${DATA_360_DISPLAY_NAME} (${result.sampleCount} samples).`
      )
      setShowData360ReimportConfirm(false)
    } catch (err: unknown) {
      toast.error(
        formatData360DisplayText(
          err instanceof Error ? err.message : `Failed to re-import from ${DATA_360_DISPLAY_NAME}.`
        )
      )
    } finally {
      setReimportBusy(false)
    }
  }, [
    id,
    pipeId,
    weldId,
    data360Meta,
    data360,
    weldNumber,
    getSection,
    refresh,
    syncSectionsVersion
  ])

  const sampleCount = useMemo(
    () =>
      uploadedAggregate
        ? uploadedAggregate.rows.reduce((acc, r) => acc + (r.sampleCount ?? 0), 0)
        : 0,
    [uploadedAggregate]
  )

  const preheatSection = useMemo(() => {
    const raw = sections.preheat ?? getSection('preheat')
    return raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null
  }, [sections, getSection])

  const preheatInterpassByPass = useMemo(() => {
    if (!weldId) return null
    const floorData = getSection('floorsheet')
    const welds = floorData && Array.isArray(floorData.welds) ? floorData.welds : []
    const currentWeld = welds.find(
      (w: unknown) => String((w as { id?: unknown })?.id) === String(weldId)
    )
    if (!currentWeld || typeof currentWeld !== 'object') return null
    const raw = (currentWeld as { preheatInterpassByPass?: unknown }).preheatInterpassByPass
    if (!raw || typeof raw !== 'object') return null
    const out: Record<string, { min: string; max: string; avg: string }> = {}
    for (const [pass, value] of Object.entries(raw as Record<string, unknown>)) {
      if (!value || typeof value !== 'object') continue
      const row = value as { min?: unknown; max?: unknown; avg?: unknown }
      out[pass] = {
        min: String(row.min ?? ''),
        max: String(row.max ?? ''),
        avg: String(row.avg ?? '')
      }
    }
    return Object.keys(out).length > 0 ? out : null
  }, [weldId, getSection, sections.floorsheet])

  const headerPills = useMemo(() => {
    let bevelLabel = ''
    let pipe1Label = ''
    let pipe2Label = ''

    if (weldId) {
      const floorData = getSection('floorsheet')
      const welds = floorData && Array.isArray(floorData.welds) ? floorData.welds : []
      const currentWeld = welds.find((w: unknown) => String((w as { id?: unknown })?.id) === String(weldId))
      if (currentWeld && typeof currentWeld === 'object') {
        const weldRecord = currentWeld as Record<string, unknown>
        const bevelSection = getSection('bevel')
        bevelLabel = bevelLabelForIndex(
          bevelSection && typeof bevelSection === 'object'
            ? (bevelSection as Record<string, unknown>)
            : null,
          normalizeBevelIndex(weldRecord.bevelIndex)
        )
        const baseSection = getSection('base')
        const descriptionsByHeat = buildDescriptionsByHeat(
          baseSection && typeof baseSection === 'object'
            ? (baseSection as Record<string, unknown>)
            : null
        )
        const { pupOne, pupTwo } = resolvePupHeatsFromWeld(weldRecord)
        pipe1Label = resolvePupDisplayLabel(pupOne, descriptionsByHeat)
        pipe2Label = resolvePupDisplayLabel(pupTwo, descriptionsByHeat)
      }
    }

    return {
      wpsNumber,
      bevelLabel,
      pipe1Label,
      pipe2Label,
      weldNumber
    }
  }, [getSection, sections, weldId, wpsNumber, weldNumber])

  const showHeaderPills = weldParametersHeaderHasAny(headerPills)

  const weldIdExtras = useMemo((): string[] => {
    const floorData = getSection('floorsheet')
    const welds = floorData && Array.isArray(floorData.welds) ? floorData.welds : []
    return Array.from(
      new Set(
        welds
          .flatMap((w: { weldId?: unknown; weldNumber?: unknown }) => [w.weldId, w.weldNumber])
          .map((s: unknown) => String(s ?? '').trim())
          .filter((s: string) => s.length > 0)
      )
    )
  }, [getSection, sections])

  const weldIdSelectOptions = useMemo(
    () => getLabelsMerged('floor_weld_id', weldIdExtras),
    [getLabelsMerged, weldIdExtras]
  )

  const persistWeldIdOnFloorsheet = useCallback(
    async (value: string) => {
      if (!weldId) return
      const trimmed = value.trim()
      if (!trimmed) return
      setWeldNumber(trimmed)
      const floorData = getSection('floorsheet')
      if (!floorData || !Array.isArray(floorData.welds)) return
      const nextWelds = floorData.welds.map((w: Record<string, unknown>) =>
        String(w.id) === String(weldId)
          ? {
              ...w,
              weldId: trimmed,
              weldNumber: trimmed,
              passRecords: Array.isArray(w.passRecords)
                ? (w.passRecords as Record<string, unknown>[]).map((r) => ({
                    ...r,
                    weldId: trimmed,
                    weldNumber: trimmed
                  }))
                : w.passRecords
            }
          : w
      )
      await updateSection('floorsheet', { ...floorData, welds: nextWelds })
    },
    [weldId, getSection, updateSection]
  )

  const filtersStorageKey =
    id && pipeId && weldId ? `wp-filters:${mode}:${id}:${pipeId}:${weldId}` : ''

  const passLabels =
    uploadFormat === 'alx-summary' && uploadedAggregate
      ? sortAlxPassLabels(Array.from(new Set(uploadedAggregate.rows.map((r) => r.pass))))
      : resolveWeldParametersPassLabels(
          wpsPassLabels.length
            ? wpsPassLabels
            : uploadedAggregate
              ? Array.from(new Set(uploadedAggregate.rows.map((r) => r.pass)))
              : Array.from(new Set(manualRows.map((r) => r.pass))),
          mode === 'upload' ? uploadedAggregate : null
        )

  const handleSwapIwmBugTypeChange = useCallback(
    async (next: boolean) => {
      setSwapIwmSaveBusy(true)
      try {
        const existingMeta = (getSection('meta') ?? {}) as Record<string, unknown>
        await updateSection('meta', { ...existingMeta, swapIwmBugType: next })
        setSwapIwmBugType(next)
        toast.success(
          next
            ? 'IWM Root CW↔CCW will swap in WPS/PQR reports; saved data unchanged'
            : 'IWM Root will keep logged CW/CCW in WPS/PQR reports'
        )
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Failed to save IWM mapping')
        throw e
      } finally {
        setSwapIwmSaveBusy(false)
      }
    },
    [getSection, updateSection]
  )

  const handleExportExcel = useCallback(async () => {
    if (!id || !pipeId || !weldId) return
    if (mode !== 'upload') {
      toast.error('Excel export requires uploaded telemetry data')
      return
    }
    setExportBusy(true)
    try {
      await exportWeldParametersExcel({
        projectId: id,
        pipeSetId: pipeId,
        weldId
      })
      toast.success('Excel export started')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Excel export failed')
    } finally {
      setExportBusy(false)
    }
  }, [id, pipeId, weldId, mode])

  if (!project || !pipeSet) {
    return (
      <Card title="Weld Parameters not found">
        <p className="text-sm text-gray-500">Return to the Project Hub to continue.</p>
        <div className="mt-4">
          <Link to="/resources">
            <Button>Go to Project Hub</Button>
          </Link>
        </div>
      </Card>
    )
  }

  return (
    <div className="flex w-full min-w-0 flex-col space-y-5" ref={pageRootRef}>
      <EditingReturnBanner returnTo={returnTo} contextLabel="Weld Parameters" />
      <PageHeader
        backTo={
          returnTo
            ? { to: returnTo, label: backToLabelFor(returnTo) }
            : {
                to:
                  id && pipeId && weldId
                    ? `/projects/${encodeURIComponent(id)}/next/${encodeURIComponent(pipeId)}/floorsheet?weldTab=${encodeURIComponent(weldId)}`
                    : id && pipeId
                      ? `/projects/${encodeURIComponent(id)}/next/${encodeURIComponent(pipeId)}/floorsheet`
                      : '/',
                label: 'Back to Floor Sheet'
              }
        }
        title="Welding Parameters"
        subtitle={
          <span className="flex min-w-0 items-center gap-x-2 truncate">
            <span className="flex-shrink-0">
              {pipeSet.nominalOd} {pipeSet.nominalOdUnit} × {pipeSet.wallThickness}{' '}
              {pipeSet.wallThicknessUnit}
            </span>
            {stationDirectionLabel ? (
              <>
                <span className="text-[#E2E5EA]">•</span>
                <span
                  data-testid="wp-station-direction"
                  className="flex-shrink-0 font-semibold text-text-grey"
                >
                  {stationDirectionLabel}
                </span>
              </>
            ) : null}
            {showHeaderPills ? (
              <>
                <span className="text-[#E2E5EA]">•</span>
                <WeldParametersHeaderPills {...headerPills} />
              </>
            ) : null}
            {refreshing ? (
              <>
                <span className="text-[#E2E5EA]">•</span>
                <span
                  data-testid="wp-refreshing"
                  className="flex-shrink-0 inline-flex items-center gap-1 rounded-md border border-blue-100 bg-blue-50/70 px-2 py-0.5 text-[10px] font-semibold text-blueprint-blue"
                >
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blueprint-blue" />
                  Refreshing…
                </span>
              </>
            ) : null}
            {manualDraft ? (
              <>
                <span className="text-[#E2E5EA]">•</span>
                <span
                  data-testid="wp-manual-draft-badge"
                  className="flex-shrink-0 inline-flex items-center gap-1 rounded-md border border-warning-200 bg-warning-50 px-2 py-0.5 text-[10px] font-semibold text-warning-700"
                >
                  <PencilSquareIcon className="h-3 w-3" />
                  Editing — unsaved
                </span>
              </>
            ) : null}
            {toleranceDirty && !loading && weldId ? (
              <>
                <span className="text-[#E2E5EA]">•</span>
                <span
                  data-testid="wp-tolerance-unsaved-badge"
                  className="flex-shrink-0 inline-flex items-center gap-1 rounded-md border border-warning-200 bg-warning-50 px-2 py-0.5 text-[10px] font-semibold text-warning-700"
                >
                  Tolerance — unsaved
                </span>
              </>
            ) : null}
          </span>
        }
        actions={
          <>
            {manualDraft ? (
              <>
                <button
                  type="button"
                  data-testid="wp-manual-cancel-btn"
                  onClick={handleRequestCancelDraft}
                  disabled={saveDraftBusy}
                  className="inline-flex items-center gap-1.5 rounded-md border border-[#E2E5EA] bg-white px-3 py-1.5 text-xs font-semibold text-heritage-grey shadow-sm transition-colors hover:border-[#C9CDD4] hover:bg-page-bg disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                  Cancel
                </button>
                <button
                  type="button"
                  data-testid="wp-manual-save-btn"
                  onClick={handleSaveManualDraft}
                  disabled={saveDraftBusy}
                  className="inline-flex items-center gap-1.5 rounded-md border border-primary-200 bg-primary-50 px-3 py-1.5 text-xs font-semibold text-primary-700 shadow-sm transition-colors hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <CheckIcon className="h-3.5 w-3.5" />
                  {saveDraftBusy ? 'Saving…' : 'Save'}
                </button>
              </>
            ) : (
              <button
                type="button"
                data-testid="wp-reupload-btn"
                onClick={() => {
                  if (isData360Upload) {
                    setShowData360ReimportConfirm(true)
                  } else if (uploadedLabel) {
                    setShowReuploadConfirm(true)
                  } else {
                    setShowReuploadMode(true)
                  }
                }}
                disabled={reimportBusy}
                className="inline-flex items-center gap-1.5 rounded-md border border-primary-200 bg-primary-50 px-3 py-1.5 text-xs font-semibold text-primary-700 shadow-sm transition-colors hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <ArrowUpTrayIcon className="h-3.5 w-3.5" />
                {reimportBusy
                  ? 'Importing…'
                  : isData360Upload
                    ? `Re-import from ${weldNumber || data360Meta?.weldName || 'weld'}`
                    : uploadedLabel
                      ? 'Re-upload data'
                      : 'Upload data'}
              </button>
            )}
            <WeldParametersOverflowMenu
              showEdit={!manualDraft && (mode === 'upload' || hasExplicitLocalSource)}
              onEdit={
                mode === 'upload' ? handleEnterManually : handleReEnterManualEdit
              }
              showDelete={Boolean(
                !manualDraft &&
                  (mode === 'upload' || hasServerUpload) &&
                  id &&
                  pipeId &&
                  weldId
              )}
              onDelete={() => setShowDeleteConfirm(true)}
              deleteDisabled={deleteBusy}
              onColumns={() => setShowColumns(true)}
              showConfigureIwmRoot={showIwmRootMapping}
              onConfigureIwmRoot={
                showIwmRootMapping ? () => setIwmRootDialogOpen(true) : undefined
              }
              iwmBusy={swapIwmSaveBusy}
              onExportExcel={handleExportExcel}
              exportBusy={exportBusy}
            />
            {showIwmRootMapping ? (
              <SwapIwmBugTypeToggle
                enabled={swapIwmBugType}
                busy={swapIwmSaveBusy}
                projectId={id}
                pipeSetId={pipeId}
                weldId={weldId}
                alxPassNames={passLabels}
                showButton={false}
                open={iwmRootDialogOpen}
                onOpenChange={setIwmRootDialogOpen}
                onChange={handleSwapIwmBugTypeChange}
              />
            ) : null}
          </>
        }
      />

      <div className="min-w-0 space-y-3">
        {loading ? (
          <PageLoader label="Loading welding parameters…" compact data-testid="wp-loading" />
        ) : (
          <>
            {mode === 'upload' && uploadedAggregate ? (
              <>
                {uploadFormat === 'alx-summary' ? <AlxSummaryInfoBanner /> : null}
                {!loading && weldId ? (
                  <WeldParametersToleranceBar
                    tolerance={tolerance}
                    onToleranceChange={setTolerance}
                    onSave={handleSaveTolerance}
                    busy={toleranceSaveBusy}
                    dirty={toleranceDirty}
                  />
                ) : null}
                <WeldParametersUploadedView
                  aggregate={uploadedAggregate}
                  passLabels={passLabels}
                  visibleColumns={visibleColumns}
                  filtersStorageKey={filtersStorageKey}
                  preheatSection={preheatSection}
                  preheatInterpassByPass={preheatInterpassByPass}
                  passAnalyticsUnavailable={uploadFormat === 'alx-summary'}
                  toleranceBoundary={toleranceBoundary}
                  pqrLimitsMode={pqrLimitsMode}
                  swapIwmBugType={showIwmRootMapping && swapIwmBugType}
                  onPassView={
                  id && pipeId && weldId && passAnalyticsAvailable
                    ? (pass) =>
                        navigate(
                          `/projects/${encodeURIComponent(id)}/next/${encodeURIComponent(
                            pipeId
                          )}/floorsheet/${encodeURIComponent(weldId)}/parameters/passes/${encodeURIComponent(pass)}`
                        )
                    : undefined
                }
              />
              </>
            ) : (
              <WeldParametersManualView
                rows={manualRows}
                passLabels={passLabels}
                visibleColumns={visibleColumns}
                filtersStorageKey={filtersStorageKey}
                preheatSection={preheatSection}
                preheatInterpassByPass={preheatInterpassByPass}
                toleranceBoundary={toleranceBoundary}
                pqrLimitsMode={pqrLimitsMode}
                swapIwmBugType={showIwmRootMapping && swapIwmBugType}
                onChange={handleCellChange}
                onTorchChange={handleTorchChange}
                // Read-only when the weld has persisted manual values AND
                // the user isn't currently editing — gives them a viewer
                // (with the eye/info column) instead of an editable form,
                // matching the post-save UX. The re-edit pencil in the
                // page header flips `manualDraft` back to true to drop
                // into the editable form. First-time manual entry
                // (`hasExplicitLocalSource === false`) keeps the existing
                // typing-with-auto-save flow because there's no saved
                // state to read-only-display yet.
                readOnly={!manualDraft && hasExplicitLocalSource}
                renderRowActions={
                  !manualDraft && hasExplicitLocalSource && id && pipeId && weldId
                    ? (pass) => {
                        const sampleCount =
                          uploadedAggregate?.rows.find((r) => r.pass === pass)
                            ?.sampleCount ?? 0
                        const disabled = sampleCount <= 0
                        const title = disabled
                          ? 'No telemetry recorded for this pass'
                          : `View ${sampleCount.toLocaleString()} samples for ${pass}`
                        return (
                          <button
                            type="button"
                            data-testid={`wp-pass-view-${pass}`}
                            disabled={disabled}
                            title={title}
                            aria-label={title}
                            onClick={() =>
                              navigate(
                                `/projects/${encodeURIComponent(id)}/next/${encodeURIComponent(
                                  pipeId
                                )}/floorsheet/${encodeURIComponent(
                                  weldId
                                )}/parameters/passes/${encodeURIComponent(pass)}`
                              )
                            }
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[#E2E5EA] bg-white text-heritage-grey transition-all duration-150 hover:border-hero-red-20 hover:bg-hero-red-10/60 hover:text-hero-red hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-[#E2E5EA] disabled:hover:bg-white disabled:hover:text-heritage-grey disabled:hover:shadow-none"
                          >
                            <EyeIcon className="h-4 w-4" aria-hidden />
                          </button>
                        )
                      }
                    : undefined
                }
              />
            )}
          </>
        )}
      </div>

      <ColumnSelectorDialog
        open={showColumns}
        value={visibleColumns}
        onChange={handleVisibleColumnsChange}
        onClose={() => setShowColumns(false)}
        preheatUnit={String(preheatSection?.preheatUnit ?? '°F').trim() || '°F'}
      />

      <ConfirmDialog
        open={showConfirm}
        title="Switch to manual entry?"
        description={CONFIRM_DESCRIPTION}
        confirmLabel="Switch to manual"
        cancelLabel="Cancel"
        tone="danger"
        onConfirm={handleConfirmSwitch}
        onCancel={handleCancelSwitch}
      />

      <ConfirmDialog
        open={showDeleteConfirm}
        title="Delete uploaded TXT data?"
        description={DELETE_CONFIRM_DESCRIPTION}
        confirmLabel="Delete data"
        cancelLabel="Cancel"
        tone="danger"
        confirmBusy={deleteBusy}
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      <ConfirmDialog
        open={showCancelDraftConfirm}
        title="Discard manual edits?"
        description={CANCEL_DRAFT_CONFIRM_DESCRIPTION}
        confirmLabel="Discard edits"
        cancelLabel="Keep editing"
        tone="danger"
        onConfirm={handleCancelManualDraft}
        onCancel={() => setShowCancelDraftConfirm(false)}
      />

      <ConfirmDialog
        open={showReuploadConfirm}
        title="Re-upload telemetry?"
        description={TELEMETRY_REPLACE_CONFIRM_DESCRIPTION(weldNumber || 'this weld')}
        confirmLabel="Continue"
        cancelLabel="Cancel"
        tone="danger"
        onConfirm={() => {
          setShowReuploadConfirm(false)
          setShowReuploadMode(true)
        }}
        onCancel={() => setShowReuploadConfirm(false)}
      />

      <ConfirmDialog
        open={showData360ReimportConfirm}
        title={`Re-import from ${DATA_360_DISPLAY_NAME}?`}
        description={DATA360_REIMPORT_CONFIRM_DESCRIPTION(
          weldNumber || data360Meta?.weldName || 'this weld'
        )}
        confirmLabel="Re-import"
        cancelLabel="Cancel"
        tone="danger"
        confirmBusy={reimportBusy}
        onConfirm={() => void handleData360Reimport()}
        onCancel={() => (reimportBusy ? undefined : setShowData360ReimportConfirm(false))}
      />

      {id && pipeId && weldId ? (
        <UnifiedWeldUploadDialog
          open={showReuploadMode}
          onClose={() => setShowReuploadMode(false)}
          projectId={id}
          pipeSetId={pipeId}
          weldId={weldId}
          floorSheetWeldLabel={weldNumber}
          currentWeldNumber={weldNumber}
          wpsPassLabels={passLabels}
          existingSummary={existingSummary}
          existingUploadFormat={uploadFormat}
          weldIdSelectOptions={weldIdSelectOptions}
          allowCreateMultiple={false}
          onWeldIdChange={(value) => {
            void persistWeldIdOnFloorsheet(value)
          }}
          onCreateWeldId={async (bare) => {
            if (!id) return ''
            const trimmed = bare.trim()
            if (!trimmed) return ''
            await addLabel('floor_weld_id', trimmed, id)
            await persistWeldIdOnFloorsheet(trimmed)
            return trimmed
          }}
          onApplyWeldId={(_, label) => {
            void persistWeldIdOnFloorsheet(label)
          }}
          onUploadComplete={(payload) =>
            handleReuploadComplete({
              ...payload,
              uploadFormat: payload.uploadFormat ?? uploadFormat
            })
          }
        />
      ) : null}
    </div>
  )
}

function formatUploadedDate(iso: string): string {
  if (!iso) return 'Recent upload'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  try {
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return d.toISOString()
  }
}

function defaultManualTorch(torches: Torch[]): Torch {
  // Single-torch passes carry the existing torch; mixed-torch passes default to
  // 'Lead' (user can change in the manual view).
  if (torches.length === 1) return torches[0]
  return 'Lead'
}

function isAggregateShape(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false
  const rows = (value as { rows?: unknown }).rows
  if (!Array.isArray(rows)) return false
  return rows.every(
    (row: any) =>
      row &&
      typeof row.pass === 'string' &&
      Array.isArray(row.torches) &&
      row.values &&
      typeof row.values === 'object'
  )
}

function upsertCellValue(
  rows: ManualRow[],
  pass: string,
  param: ParameterKey,
  agg: ManualAgg,
  value: number | null
): ManualRow[] {
  let found = false
  const next = rows.map((row) => {
    if (row.pass !== pass) return row
    found = true
    const prevValues = (row.values || {}) as ManualRow['values']
    const prevParam = (prevValues[param] || {}) as Record<string, number | null>
    return {
      ...row,
      values: {
        ...prevValues,
        [param]: { ...prevParam, [agg]: value }
      }
    }
  })
  if (!found) {
    next.push({
      pass,
      torch: 'Lead',
      values: { [param]: { [agg]: value } } as ManualRow['values']
    })
  }
  return next
}

function upsertRowTorch(rows: ManualRow[], pass: string, torch: Torch): ManualRow[] {
  let found = false
  const next = rows.map((row) => {
    if (row.pass !== pass) return row
    found = true
    return { ...row, torch }
  })
  if (!found) {
    next.push({ pass, torch, values: {} })
  }
  return next
}

function parsePreheatNum(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : null
}
